/**
 * Local data cache for the big collections (fleet, contracts, airport, archive,
 * maintenance, invygo). Backed by IndexedDB so it survives closing the tab and
 * has no practical size limit; the previous sessionStorage version was wiped
 * on every new session, so every visit re-read whole collections from
 * Firestore and burned the daily read quota.
 *
 * Same API as before (all methods are async): saveX / loadX per collection,
 * isCacheValid(filename, hours), clearSpecificCache / clearCache, exportCache.
 * Requests on one connection run in order, so a clear followed by a save
 * (the usual "refresh" sequence) never leaves stale data behind.
 */

const DB_NAME = 'yelo-cache';
const STORE = 'files';
const CACHE_FILES = {
    CONTRACTS: 'contracts.json',
    MAINTENANCE: 'maintenance.json',
    FLEET: 'fleet.json',
    TRAFFIC: 'traffic.json',
    ARCHIVE: 'archive.json',
    INVYGO: 'invygo.json',
    AIRPORT: 'airport.json',
    CONTACTS: 'contacts.json',
    META: 'cache-meta.json'
};

let dbPromise = null;
const openDb = () => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB unavailable')); return; }
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => { if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE); };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        req.onblocked = () => reject(new Error('IndexedDB blocked'));
    }).catch((e) => { dbPromise = null; throw e; });
    return dbPromise;
};

const request = (mode, run) => openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = run(tx.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
}));

// One in-memory copy per file: pages read the same cache many times per visit
const memory = new Map();

export const jsonCacheService = {
    /**
     * Save a collection snapshot: { timestamp, fullReadAt, data, count }. Returns false when storage fails.
     * `timestamp` = when the copy was last brought up to date (a whole read OR a merge of the changed rows);
     * `fullReadAt` = the last WHOLE read - only that drops deleted rows, so the pages read everything again once
     * it is a day old (a merge keeps the previous fullReadAt; a copy without one counts as never read whole).
     * `asOf` (the engine's store, USE_CONTRACTS_V2): the engine's clock at the read the copy came from - its next
     * "changed since" starts there, never at the browser's clock; a copy saved without it is read whole again.
     */
    async saveToFile(filename, data, { full = false, asOf = null } = {}) {
        const now = new Date().toISOString();
        const prev = memory.get(filename);
        const payload = { timestamp: now, fullReadAt: full ? now : (prev && prev.fullReadAt) || null, data, count: Array.isArray(data) ? data.length : 1 };
        if (asOf) payload.asOf = asOf;
        memory.set(filename, payload);
        try {
            await request('readwrite', (store) => store.put(payload, filename));
            return true;
        } catch (error) {
            return false;
        }
    },

    /** The saved snapshot ({ timestamp, data, count }) or null. */
    async loadFromFile(filename) {
        if (memory.has(filename)) return memory.get(filename);
        try {
            const payload = await request('readonly', (store) => store.get(filename));
            if (payload) memory.set(filename, payload);
            return payload || null;
        } catch (error) {
            return null;
        }
    },

    async saveContracts(contracts, opts) { return this.saveToFile(CACHE_FILES.CONTRACTS, contracts, opts); },
    async loadContracts() { const r = await this.loadFromFile(CACHE_FILES.CONTRACTS); return r ? r.data : null; },
    async saveMaintenance(maintenance, opts) { return this.saveToFile(CACHE_FILES.MAINTENANCE, maintenance, opts); },
    async loadMaintenance() { const r = await this.loadFromFile(CACHE_FILES.MAINTENANCE); return r ? r.data : null; },
    async saveFleet(fleet, opts) { return this.saveToFile(CACHE_FILES.FLEET, fleet, opts); },
    async loadFleet() { const r = await this.loadFromFile(CACHE_FILES.FLEET); return r ? r.data : null; },
    async saveTraffic(traffic) { return this.saveToFile(CACHE_FILES.TRAFFIC, traffic); },
    async loadTraffic() { const r = await this.loadFromFile(CACHE_FILES.TRAFFIC); return r ? r.data : null; },
    async saveArchive(archive) { return this.saveToFile(CACHE_FILES.ARCHIVE, archive); },
    async loadArchive() { const r = await this.loadFromFile(CACHE_FILES.ARCHIVE); return r ? r.data : null; },
    async saveInvygo(invygo) { return this.saveToFile(CACHE_FILES.INVYGO, invygo); },
    async loadInvygo() { const r = await this.loadFromFile(CACHE_FILES.INVYGO); return r ? r.data : null; },
    async saveAirport(airport, opts) { return this.saveToFile(CACHE_FILES.AIRPORT, airport, opts); },
    async loadAirport() { const r = await this.loadFromFile(CACHE_FILES.AIRPORT); return r ? r.data : null; },
    async saveContacts(chunks) { return this.saveToFile(CACHE_FILES.CONTACTS, chunks); },
    async loadContacts() { const r = await this.loadFromFile(CACHE_FILES.CONTACTS); return r ? r.data : null; },

    /** True when the snapshot exists and is younger than maxAgeHours. */
    async isCacheValid(filename, maxAgeHours = 24) {
        const result = await this.loadFromFile(filename);
        if (!result || !result.timestamp) return false;
        const ageHours = (Date.now() - new Date(result.timestamp).getTime()) / 3600000;
        return ageHours >= 0 && ageHours < maxAgeHours;
    },

    /**
     * Keep the snapshot but date it at least `minutes` back, so the next read refreshes it (the
     * contracts read then fetches only what changed since - the row just saved included) instead of
     * dropping it and reading the whole collection again. An older snapshot keeps its older date.
     */
    async markStale(filename, minutes = 16) {
        const payload = await this.loadFromFile(filename);
        if (!payload || !payload.timestamp) return false;
        const stale = Date.now() - minutes * 60000;
        if (new Date(payload.timestamp).getTime() <= stale) return true;
        const next = { ...payload, timestamp: new Date(stale).toISOString() };
        memory.set(filename, next);
        try { await request('readwrite', (store) => store.put(next, filename)); return true; } catch (e) { return false; }
    },

    /** Drop one snapshot (fire-and-forget safe: the next save is queued after it). */
    clearSpecificCache(filename) {
        memory.delete(filename);
        return request('readwrite', (store) => store.delete(filename)).catch(() => false);
    },

    clearMaintenanceCache() { return this.clearSpecificCache(CACHE_FILES.MAINTENANCE); },

    clearCache() {
        memory.clear();
        return request('readwrite', (store) => store.clear()).catch(() => false);
    },

    /** Download the cached contracts / maintenance / fleet as one JSON file. */
    async exportCache() {
        try {
            const [contracts, maintenance, fleet] = await Promise.all([this.loadContracts(), this.loadMaintenance(), this.loadFleet()]);
            const exportData = {
                timestamp: new Date().toISOString(),
                contracts: contracts || [], maintenance: maintenance || [], fleet: fleet || [],
                stats: { contractsCount: contracts?.length || 0, maintenanceCount: maintenance?.length || 0, fleetCount: fleet?.length || 0 }
            };
            const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `yelo-cache-${new Date().toISOString().slice(0, 10)}.json`;
            link.click();
            URL.revokeObjectURL(url);
            return true;
        } catch (error) {
            return false;
        }
    }
};
